import React, { useEffect, useState } from "react";
import MetaTags from "react-meta-tags";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Label
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import Breadcrumbs from "components/Common/Breadcrumb";
import Datatable from "components/Datatables/datatable";
import { get, post } from "helpers/api_helper";
import Timezone from "utils/timezone";

const PayrollReport = () => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  const [filterData, setFilterData] = useState({
    branch_id: "all",
    employee_id: "all",
    month: "all",
    year: "all",
    department: "all",
    role: "all",
    dates: []
  });

  const [selectedDates, setSelectedDates] = useState([]);

  const months = [
    { value: "all", label: "All Months" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = [
    { value: "all", label: "All Years" },
    ...Array.from({ length: 5 }, (_, i) => ({
      value: new Date().getFullYear() - i,
      label: (new Date().getFullYear() - i).toString(),
    }))
  ];

  const fetchFilters = async () => {
    try {
      const branchRes = await get("api/selectors/getBranchs");
      if (branchRes.status === 200 || branchRes.status === true) {
        setBranchOptions([
          { value: "all", label: "All Branches" },
          ...branchRes.response.map(b => ({ value: b.id, label: b.title }))
        ]);
      }

      const deptRes = await get("api/masters?access_type=m01");
      if (deptRes.status === 200 || deptRes.status === true) {
        const rows = deptRes.response?.rows || deptRes.response || [];
        setDeptOptions([
          { value: "all", label: "All Departments" },
          ...rows.map(d => ({ value: d.code, label: d.title }))
        ]);
      }

      const roleRes = await get("api/masters?access_type=m13");
      if (roleRes.status === 200 || roleRes.status === true) {
        const rows = roleRes.response?.rows || roleRes.response || [];
        setRoleOptions([
          { value: "all", label: "All Roles" },
          ...rows.map(r => ({ value: r.code, label: r.title }))
        ]);
      }

      fetchEmployees("all");
    } catch (error) {
      console.error("Error loading filters", error);
    }
  };

  const fetchEmployees = async (branchVal) => {
    try {
      const empRes = await post("api/selectors/getEmployees", { branch: branchVal });
      if (empRes.status === 200 || empRes.status === true) {
        setEmployeeOptions([
          { value: "all", label: "All Employees" },
          ...empRes.response.map(e => ({ value: e.value, label: e.label }))
        ]);
      }
    } catch (error) {
      console.error("Error loading employees", error);
    }
  };

  const handleBranchChange = (selected) => {
    const val = selected ? selected.value : "all";
    setFilterData(prev => ({ ...prev, branch_id: val, employee_id: "all" }));
    fetchEmployees(val);
  };

  const loadReportData = async (filters = filterData) => {
    setIsLoading(true);
    try {
      const res = await post("api/reports/payroll", filters);
      if (res.status === 200 || res.status === true) {
        setRowData(res.response?.data || res.response || []);
      }
    } catch (error) {
      console.error("Failed to load payroll report", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchFilters();
    loadReportData();
  }, []);

  const handleFilterSubmit = () => {
    loadReportData();
  };

  const handleReset = () => {
    const resetObj = {
      branch_id: "all",
      employee_id: "all",
      month: "all",
      year: "all",
      department: "all",
      role: "all",
      dates: []
    };
    setFilterData(resetObj);
    setSelectedDates([]);
    fetchEmployees("all");
    loadReportData(resetObj);
  };

  // Metrics
  const totals = rowData.reduce((acc, row) => {
    acc.basic += parseFloat(row.basic_salary || 0);
    acc.allowance += parseFloat(row.total_allowance || 0);
    acc.deduction += parseFloat(row.total_deduction || 0);
    acc.net += parseFloat(row.net_salary || 0);
    return acc;
  }, { basic: 0, allowance: 0, deduction: 0, net: 0 });

  const metrics = [
    { title: "Total Basic Salary", value: `₹${totals.basic.toFixed(2)}`, color: "#3d1e08" },
    { title: "Total Allowances", value: `+₹${totals.allowance.toFixed(2)}`, color: "#159858" },
    { title: "Total Deductions", value: `-₹${totals.deduction.toFixed(2)}`, color: "#ff3366" },
    { title: "Total Net Pay", value: `₹${totals.net.toFixed(2)}`, color: "#159858" },
    { title: "Total Records", value: rowData.length, color: "#777" }
  ];

  const tableColumns = [
    {
      dataField: "id",
      text: "#",
      sort: true,
      formatter: (cell, row, index) => index + 1
    },
    {
      dataField: "employee_name",
      text: "Employee",
      sort: true,
      formatter: (cell, row) => (
        <div>
          <div style={{ fontWeight: "bold", color: "#3d1e08" }}>{row.employee_name}</div>
          <div style={{ fontSize: "11px", color: "#777" }}>ID: {row.employee_id}</div>
        </div>
      )
    },
    {
      dataField: "branch_name",
      text: "Branch",
      sort: true,
      formatter: (cell, row) => row.branch_name || "---"
    },
    {
      dataField: "department_name",
      text: "Department",
      sort: true,
      formatter: (cell, row) => row.department_name || "---"
    },
    {
      dataField: "role_name",
      text: "Role",
      sort: true,
      formatter: (cell, row) => row.role_name || "---"
    },
    {
      dataField: "month_year",
      text: "Month/Year",
      sort: true
    },
    {
      dataField: "basic_salary",
      text: "Basic Salary",
      sort: true,
      formatter: (cell) => `₹${parseFloat(cell || 0).toFixed(2)}`
    },
    {
      dataField: "total_allowance",
      text: "Allowances",
      sort: true,
      formatter: (cell, row) => (
        <div>
          <span className="text-success" style={{ fontWeight: "bold" }}>
            +₹{parseFloat(cell || 0).toFixed(2)}
          </span>
          {row.items && row.items.length > 0 && (
            <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>
              {row.items.filter(i => i.type === "Earning").map((i, idx) => (
                <div key={idx}>{i.name}: ₹{parseFloat(i.amount).toFixed(2)}</div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      dataField: "total_deduction",
      text: "Deductions",
      sort: true,
      formatter: (cell, row) => (
        <div>
          <span className="text-danger" style={{ fontWeight: "bold" }}>
            -₹{parseFloat(cell || 0).toFixed(2)}
          </span>
          {row.items && row.items.length > 0 && (
            <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>
              {row.items.filter(i => i.type === "Deduction").map((i, idx) => (
                <div key={idx}>{i.name}: ₹{parseFloat(i.amount).toFixed(2)}</div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      dataField: "net_salary",
      text: "Net Pay",
      sort: true,
      formatter: (cell) => <strong className="text-success">₹{parseFloat(cell || 0).toFixed(2)}</strong>
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: (cell) => (
        <span className={`badge ${cell === "Paid" ? "bg-success" : "bg-warning"}`} style={{ padding: "5px 10px" }}>
          {cell}
        </span>
      )
    },
    {
      dataField: "created_at",
      text: "Generated At",
      sort: true,
      formatter: (cell) => cell ? new Date(cell).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) : "---"
    }
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Reports | Payroll Report</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Reports" breadcrumbItem="Payroll Report" />

          {/* Filters Card */}
          <div className="cardfilters">
            <div className="text-uppercase mb-3" style={{ fontWeight: "bold", fontSize: "12px", color: "#555" }}>
              Payroll Filter
            </div>
            <Row className="g-3">
              <Col md={3}>
                <Label style={{ fontSize: "12px" }}>Branch</Label>
                <Select
                  placeholder="Select Branch"
                  options={branchOptions}
                  value={branchOptions.find(b => b.value === filterData.branch_id) || null}
                  onChange={handleBranchChange}
                />
              </Col>
              <Col md={3}>
                <Label style={{ fontSize: "12px" }}>Employee</Label>
                <Select
                  placeholder="Select Employee"
                  options={employeeOptions}
                  value={employeeOptions.find(e => e.value === filterData.employee_id) || null}
                  onChange={(selected) => setFilterData(prev => ({ ...prev, employee_id: selected ? selected.value : "all" }))}
                />
              </Col>
              <Col md={2}>
                <Label style={{ fontSize: "12px" }}>Month</Label>
                <Select
                  placeholder="Month"
                  options={months}
                  value={months.find(m => m.value === filterData.month) || null}
                  onChange={(selected) => setFilterData(prev => ({ ...prev, month: selected ? selected.value : "all" }))}
                />
              </Col>
              <Col md={2}>
                <Label style={{ fontSize: "12px" }}>Year</Label>
                <Select
                  placeholder="Year"
                  options={years}
                  value={years.find(y => y.value === filterData.year) || null}
                  onChange={(selected) => setFilterData(prev => ({ ...prev, year: selected ? selected.value : "all" }))}
                />
              </Col>
              <Col md={2}>
                <Label style={{ fontSize: "12px" }}>Department</Label>
                <Select
                  placeholder="Select Department"
                  options={deptOptions}
                  value={deptOptions.find(d => d.value === filterData.department) || null}
                  onChange={(selected) => setFilterData(prev => ({ ...prev, department: selected ? selected.value : "all" }))}
                />
              </Col>
              <Col md={3}>
                <Label style={{ fontSize: "12px" }}>Role</Label>
                <Select
                  placeholder="Select Role"
                  options={roleOptions}
                  value={roleOptions.find(r => r.value === filterData.role) || null}
                  onChange={(selected) => setFilterData(prev => ({ ...prev, role: selected ? selected.value : "all" }))}
                />
              </Col>
              <Col md={4}>
                <Label style={{ fontSize: "12px" }}>Date Range (Generation Date)</Label>
                <Flatpickr
                  className="form-control d-block"
                  placeholder="DD-MM-YYYY"
                  options={{
                    mode: "range",
                    dateFormat: "d-m-Y"
                  }}
                  value={selectedDates}
                  onChange={(dates) => {
                    const formattedDates = dates.map(d => Timezone(d));
                    setSelectedDates(dates);
                    setFilterData(prev => ({ ...prev, dates: formattedDates }));
                  }}
                />
              </Col>
              <Col md={5} className="d-flex align-items-end justify-content-start">
                <Button
                  color="success"
                  className="me-2 px-4"
                  onClick={handleFilterSubmit}
                  disabled={isLoading}
                  style={{ backgroundColor: "#159858", borderColor: "#159858" }}
                >
                  Filter
                </Button>
                <Button
                  color="secondary"
                  className="px-4"
                  onClick={handleReset}
                  disabled={isLoading}
                  style={{ backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff" }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </div>

          {/* Metrics Row */}
          <Row className="mb-4">
            {metrics.map((m, idx) => (
              <Col className="col-md col-sm-6 mb-3" key={idx}>
                <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: `4px solid ${m.color}`, borderRadius: "8px" }}>
                  <CardBody className="p-3">
                    <div className="text-muted text-uppercase text-truncate mb-2" style={{ fontSize: "11px", fontWeight: "600", letterSpacing: "0.5px" }}>{m.title}</div>
                    {!isLoading ? (
                      <h4 className="mb-0 font-weight-bold" style={{ color: m.color, fontWeight: "700" }}>{m.value}</h4>
                    ) : (
                      <h4 className="mb-0 placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </h4>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Report Data Table */}
          <Row>
            <Col xl="12">
              <Card>
                <CardBody>
                  <Datatable
                    keyField="id"
                    title="Payroll Summary Report"
                    isAdd={false}
                    excel={true}
                    csv={false}
                    pdf={false}
                    isSearch={true}
                    columns={tableColumns}
                    rows={rowData}
                    rowsLength={rowData.length}
                    loading={isLoading}
                    is_remote={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PayrollReport;
